import React, { useMemo, useState } from 'react'
import { AlertCircle, Loader2, MailCheck, MailX } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { resubscribeAbandonedEmail, resubscribeMarketingEmail, unsubscribeMarketingEmail } from '@/services/api'

type UnsubscribeStatus = 'success' | 'resubscribed' | 'error' | 'confirm'
type QueryErrorCode = 'missing_token' | 'invalid_token' | null
type CardTone = 'success' | 'error'

const parseStatus = (value: string | null): UnsubscribeStatus | null => {
	if (value === 'success' || value === 'resubscribed' || value === 'error' || value === 'confirm') {
		return value
	}
	return null
}

const parseErrorCode = (value: string | null): QueryErrorCode => {
	if (value === 'missing_token' || value === 'invalid_token') {
		return value
	}
	return null
}

const getPageContent = (
	status: UnsubscribeStatus | null,
	errorCode: QueryErrorCode,
	hasToken: boolean,
	marketing = false
): { title: string; body: string; tone: CardTone } => {
	if (status === 'confirm' && hasToken && marketing) return {
		title: 'Unsubscribe from Kibu emails?', body: 'Confirm below to stop Kibu news and offers. You will still receive essential order updates.', tone: 'success',
	}
	if (status === 'success') {
		return {
			title: 'You are unsubscribed',
			body: marketing ? 'You won’t receive Kibu news and offers anymore. Essential order updates will continue.' : 'You won’t receive abandoned basket reminder emails anymore.',
			tone: 'success',
		}
	}

	if (status === 'resubscribed') {
		return {
			title: 'You are resubscribed',
			body: marketing ? 'You can receive Kibu news and offers again.' : 'You can receive abandoned basket reminder emails again.',
			tone: 'success',
		}
	}

	if (status === 'error' && errorCode === 'invalid_token') {
		return {
			title: 'Link expired or invalid',
			body: 'This link is no longer valid.',
			tone: 'error',
		}
	}

	if (status === 'error' && errorCode === 'missing_token') {
		return {
			title: 'Link incomplete',
			body: 'This unsubscribe link is missing required information.',
			tone: 'error',
		}
	}

	if (!hasToken) {
		return {
			title: 'Link incomplete',
			body: 'This unsubscribe link is missing required information.',
			tone: 'error',
		}
	}

	return {
		title: 'Link expired or invalid',
		body: 'This link is no longer valid.',
		tone: 'error',
	}
}

const AbandonedUnsubscribe: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams()
	const token = searchParams.get('token')
	const channel = searchParams.get('channel')
	const statusFromQuery = parseStatus(searchParams.get('status'))
	const errorCode = parseErrorCode(searchParams.get('error'))
	const [statusOverride, setStatusOverride] = useState<UnsubscribeStatus | null>(null)
	const [isResubscribing, setIsResubscribing] = useState(false)
	const [actionMessage, setActionMessage] = useState('')
	const [actionError, setActionError] = useState('')
	const [isUnsubscribing, setIsUnsubscribing] = useState(false)

	const currentStatus = statusOverride ?? statusFromQuery
	const content = useMemo(
		() => getPageContent(currentStatus, errorCode, Boolean(token), channel === 'marketing'),
		[currentStatus, errorCode, token, channel]
	)
	const canResubscribe = currentStatus === 'success' && Boolean(token)
	const handleUnsubscribe = async () => {
		if (!token || isUnsubscribing) return
		setIsUnsubscribing(true); setActionError('')
		try {
			await unsubscribeMarketingEmail(token)
			setStatusOverride('success')
			const next = new URLSearchParams(searchParams); next.set('status', 'success')
			setSearchParams(next, { replace: true })
		} catch (error) { setActionError(error instanceof Error ? error.message : 'Unable to update your preference.') }
		finally { setIsUnsubscribing(false) }
	}

	const handleResubscribe = async () => {
		if (!token) {
			setActionError('This unsubscribe link is missing required information.')
			return
		}

		setIsResubscribing(true)
		setActionError('')
		setActionMessage('')

		try {
			const response = channel === 'marketing'
				? await resubscribeMarketingEmail(token)
				: await resubscribeAbandonedEmail(token)
			setStatusOverride('resubscribed')
			setActionMessage(response.message || (channel === 'marketing' ? 'You can receive marketing emails again.' : 'You can receive abandoned basket reminder emails again.'))

			const nextParams = new URLSearchParams(searchParams)
			nextParams.set('status', 'resubscribed')
			nextParams.delete('error')
			setSearchParams(nextParams, { replace: true })
		} catch (error) {
			setActionError(error instanceof Error ? error.message : 'Unable to update your preference right now.')
		} finally {
			setIsResubscribing(false)
		}
	}

	const cardClasses = content.tone === 'error'
		? 'bg-red-50 border-2 border-red-200'
		: 'bg-green-50 border-2 border-green-200'

	const iconClasses = content.tone === 'error'
		? 'bg-red-100 text-red-600'
		: 'bg-green-100 text-green-700'

	const StatusIcon = content.tone === 'error' ? AlertCircle : MailCheck

	return (
		<div className="min-h-screen bg-background">
			<div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
				<div className="text-center mb-10">
					<div className="inline-flex items-center justify-center w-20 h-20 bg-orange bg-opacity-10 rounded-full mb-6">
						<MailX className="w-10 h-10 text-orange" />
					</div>
					<h1 className="text-4xl md:text-5xl font-headline font-bold text-black mb-4">
						Email Preferences
					</h1>
					<p className="text-lg text-gray-600 max-w-xl mx-auto">
						{channel === 'marketing' ? 'Manage your Kibu news and offers.' : 'Manage whether you receive abandoned basket reminder emails.'}
					</p>
				</div>

				<div className={`rounded-lg p-8 text-center ${cardClasses}`}>
					<div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${iconClasses}`}>
						<StatusIcon className="w-8 h-8" />
					</div>
					<h2 className="text-2xl font-semibold text-black mb-2">
						{content.title}
					</h2>
					<p className="text-gray-700">
						{actionMessage || content.body}
					</p>

					{currentStatus === 'confirm' && channel === 'marketing' && token && <button type="button" onClick={() => void handleUnsubscribe()} disabled={isUnsubscribing} className="mt-6 rounded-lg bg-orange px-5 py-3 text-white disabled:opacity-60">{isUnsubscribing ? 'Updating…' : 'Confirm unsubscribe'}</button>}
					{canResubscribe && (
						<button
							type="button"
							onClick={handleResubscribe}
							disabled={isResubscribing}
							className="mt-6 inline-flex items-center justify-center gap-2 rounded-lg bg-orange px-5 py-3 text-white font-headline transition-colors hover:bg-orange/90 disabled:opacity-60 disabled:cursor-not-allowed"
						>
							{isResubscribing && <Loader2 className="h-4 w-4 animate-spin" />}
							{isResubscribing ? 'Resubscribing...' : 'Resubscribe'}
						</button>
					)}

					{actionError && (
						<div className="mt-4 rounded-lg border border-red-200 bg-red-100 px-4 py-3 text-sm text-red-700">
							{actionError}
						</div>
					)}
				</div>

				<p className="mt-6 text-center text-sm text-gray-600">
					{channel === 'marketing' ? 'This preference affects Kibu news and offers. Essential order updates will continue.' : 'This preference only affects abandoned basket reminder emails, not transactional order emails.'}
				</p>
			</div>
		</div>
	)
}

export default AbandonedUnsubscribe
